import { Container, Graphics, Sprite, Texture, CanvasSource } from 'pixi.js';
import type { TerrainTheme } from '../terrain/bitmap';

/** [skyTop, horizon, sunGlow] per terrain theme, as CSS color strings. */
const SKY: Record<string, [string, string, string]> = {
  dirt: ['#2f6bb0', '#a6d6ef', '#fff3c0'],
  jungle: ['#2b6ba2', '#9fdde2', '#eafff0'],
  snow: ['#6a92c4', '#dcecf8', '#ffffff'],
  desert: ['#3f79c0', '#f3d9a0', '#ffe6a0'],
  hell: ['#1a0606', '#7c1d12', '#ff5a2a'],
  alien: ['#180a2e', '#5a2a86', '#c98bff'],
};

interface Cloud {
  g: Graphics;
  speed: number;
}

/**
 * Screen-space gradient sky behind the world (replaces the flat app
 * background). The gradient + sun glow are painted to an offscreen 2D canvas
 * and shown via a Sprite (batches in normal scene order, unlike a Pixi
 * gradient fill which floated to the front). Clouds/stars drift on top.
 */
export class SkyBackground {
  container: Container;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private sprite: Sprite;
  private cloudLayer: Container;
  private clouds: Cloud[] = [];
  private theme: TerrainTheme = 'dirt';
  private w = 0;
  private h = 0;

  constructor() {
    this.container = new Container();
    this.container.eventMode = 'none';
    this.canvas = document.createElement('canvas');
    this.canvas.width = 2;
    this.canvas.height = 2;
    this.ctx = this.canvas.getContext('2d')!;
    const source = new CanvasSource({ resource: this.canvas });
    this.sprite = new Sprite(new Texture({ source }));
    this.cloudLayer = new Container();
    this.container.addChild(this.sprite, this.cloudLayer);
  }

  setTheme(theme: TerrainTheme): void {
    this.theme = theme;
    this.redraw();
    this.buildClouds();
  }

  resize(width: number, height: number): void {
    this.w = Math.max(2, Math.ceil(width));
    this.h = Math.max(2, Math.ceil(height));
    this.redraw();
    this.buildClouds();
  }

  private palette(): [string, string, string] {
    return SKY[this.theme] ?? SKY.dirt;
  }

  private redraw(): void {
    if (this.w <= 0 || this.h <= 0) return;
    const [top, horizon, glow] = this.palette();
    const { ctx, w, h } = this;
    this.canvas.width = w;
    this.canvas.height = h;

    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, top);
    grad.addColorStop(0.62, mix(top, horizon, 0.7));
    grad.addColorStop(1, horizon);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Soft sun/horizon glow.
    const cx = w * 0.72;
    const cy = h * 0.16;
    const r = Math.max(w, h) * 0.6;
    const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    rg.addColorStop(0, hexToRgba(glow, 0.4));
    rg.addColorStop(0.5, hexToRgba(glow, 0.12));
    rg.addColorStop(1, hexToRgba(glow, 0));
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, w, h);

    this.sprite.texture.source.update();
  }

  private buildClouds(): void {
    this.cloudLayer.removeChildren().forEach((c) => c.destroy());
    this.clouds = [];
    if (this.w <= 0) return;

    const isNight = this.theme === 'hell' || this.theme === 'alien';
    if (isNight) {
      const star = new Graphics();
      for (let i = 0; i < 70; i++) {
        star
          .circle(Math.random() * this.w, Math.random() * this.h * 0.7, Math.random() * 1.4 + 0.4)
          .fill({ color: 0xffffff, alpha: 0.2 + Math.random() * 0.5 });
      }
      this.cloudLayer.addChild(star);
      return;
    }

    for (let i = 0; i < 5; i++) {
      const scale = 0.7 + Math.random() * 0.9;
      const g = this.makeCloud(scale);
      g.x = Math.random() * this.w;
      g.y = this.h * (0.08 + Math.random() * 0.32);
      g.alpha = 0.85;
      this.cloudLayer.addChild(g);
      this.clouds.push({ g, speed: 4 + Math.random() * 8 });
    }
  }

  private makeCloud(scale: number): Graphics {
    const g = new Graphics();
    const blobs: [number, number, number][] = [
      [0, 6, 18],
      [16, 2, 22],
      [36, 6, 18],
      [12, -6, 15],
      [26, -4, 14],
    ];
    for (const [dx, dy, r] of blobs) {
      g.ellipse(dx * scale, dy * scale, r * scale, r * scale * 0.72).fill({
        color: 0xffffff,
        alpha: 0.9,
      });
    }
    return g;
  }

  /** Drift the clouds slowly across the sky (time-based, wraps around). */
  update(deltaTime: number): void {
    for (const c of this.clouds) {
      c.g.x += c.speed * deltaTime;
      if (c.g.x > this.w + 80) c.g.x = -80;
    }
  }

  destroy(): void {
    this.sprite.texture.destroy(true);
    this.container.destroy({ children: true });
  }
}

function hexToRgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}, ${a})`;
}

function mix(a: string, b: string, t: number): string {
  const na = parseInt(a.slice(1), 16);
  const nb = parseInt(b.slice(1), 16);
  const r = Math.round(((na >> 16) & 0xff) + (((nb >> 16) & 0xff) - ((na >> 16) & 0xff)) * t);
  const g = Math.round(((na >> 8) & 0xff) + (((nb >> 8) & 0xff) - ((na >> 8) & 0xff)) * t);
  const bl = Math.round((na & 0xff) + ((nb & 0xff) - (na & 0xff)) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}
